import { GlobeAltIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { truncateString } from 'utils/formatting'
import WatchlistButton from '../watchlist/WatchlistButton'
import { useCallback, useState } from 'react'
import { DescriptionModal } from './TokenDetails'
import { ExternalLink } from '@components/shared/ExternalLink'
import { XIcon } from '../icons/XIcon'
import TelegramIcon from '../icons/TelegramIcon'
import Tooltip from '../shared/Tooltip'
import { useBirdeyeTokenOverview } from 'hooks/useBirdeyeTokenOverview'
import SheenLoader from '../loading/SheenLoader'
import RugcheckLevel from './RugcheckLevel'
import ImageWithFallback from '../image/ImageWithFallback'
import QuoteTokenSelect from './QuoteTokenSelect'
import store from 'stores/store'
import SearchModal from '@components/modals/SearchModal'
import { TokenMetadataItem } from 'types/token'
import { fetchMetadataForMints } from 'utils/tokens'
import { useTranslation } from 'next-i18next'
import { clsx } from 'clsx'
import { TokenAddress } from '@components/shared/TokenAddress'
import { useViewport } from 'hooks/useViewport'

const set = store.getState().set

const TokenInfo = () => {
  const { t } = useTranslation('token-page')
  const [showDescModal, setShowDescModal] = useState(false)
  const [showSelectList, setShowSelectList] = useState(false)
  const baseToken = store((s) => s.market.baseToken)
  const quoteToken = store((s) => s.market.quoteToken)
  const inputTokenMint = store((s) => s.swap.inputToken.mint)
  const { data: tokenOverview, isInitialLoading: loadingOverview } =
    useBirdeyeTokenOverview()
  const { extensions } = tokenOverview ?? {}
  const { isMobile } = useViewport()

  const handleSelect = useCallback(
    async (mint: string, token?: TokenMetadataItem) => {
      if (!token) {
        const connection = store.getState().connection
        token = (
          await fetchMetadataForMints([mint], undefined, connection)
        )?.[0]
      }

      if (token) {
        set((state) => {
          state.market.quoteToken = token
          if (inputTokenMint === baseToken.mint) {
            state.swap.outputToken = token
          } else {
            state.swap.inputToken = token
          }
        })
      }
      setShowSelectList(false)
    },
    [baseToken, inputTokenMint],
  )

  const descriptionToShow = baseToken.description || extensions?.description
  const websiteToShow = baseToken.website || extensions?.website
  const twitterToShow = baseToken.twitter || extensions?.twitter
  const telegramToShow = baseToken.telegram || extensions?.telegram

  return (
    <>
      <div className="flex flex-col items-center pb-4 md:flex-row md:justify-between lg:pb-0">
        <div className="flex w-full items-start sm:items-center">
          <div className="mr-2 size-10 shrink-0 sm:mr-4 sm:size-16">
            <ImageWithFallback
              height={isMobile ? 40 : 64}
              width={isMobile ? 40 : 64}
              className="size-10 sm:size-16"
              src={baseToken.image_url}
              alt={`${baseToken.symbol ?? ''} token logo`}
            />
          </div>
          <div>
            <div>
              <div className="flex items-center gap-1">
                <h1 className="whitespace-nowrap text-xl leading-none md:text-2xl">
                  {baseToken.symbol || '–'}
                </h1>
                <span className="text-xl leading-none text-th-fgd-3 md:text-2xl">
                  /
                </span>
                <QuoteTokenSelect
                  symbol={quoteToken.symbol}
                  showList={setShowSelectList}
                />
              </div>
              <div className="mb-2 inline-flex w-full items-center md:mt-1">
                {loadingOverview ? (
                  <SheenLoader>
                    <div className="mx-auto h-4 w-44 bg-th-bkg-2" />
                  </SheenLoader>
                ) : (
                  <span className="text-xs text-th-fgd-3">
                    {`${baseToken.name || '???'} ${
                      descriptionToShow != null &&
                      descriptionToShow.trim().length > 0
                        ? ` – ${truncateString(descriptionToShow, 60)}`
                        : ''
                    }`}
                    {(descriptionToShow?.length ?? 0) > 60 ? (
                      <button
                        className="inline text-th-fgd-2 focus:outline-none"
                        onClick={() => setShowDescModal(true)}
                      >
                        {' '}
                        {t('info.more')}
                      </button>
                    ) : null}
                  </span>
                )}
              </div>
              <div className="flex flex-row items-center space-x-3">
                <TokenAddress className="flex items-center" token={baseToken} />
                <RugcheckLevel />
              </div>
            </div>
          </div>
        </div>
        <div className={`mt-3 flex w-full items-center justify-end md:mt-0`}>
          <div className={`flex items-center space-x-3`}>
            <WatchlistButton
              tokenData={{
                mint: baseToken.mint,
                symbol: baseToken.symbol,
                name: baseToken.name,
                image_url: baseToken.image_url,
              }}
              large
            />
            <Tooltip content={t('info.social.x-account')}>
              <ExternalLink
                href={twitterToShow || ''}
                className={clsx(
                  'text-th-fgd-2',
                  !twitterToShow && 'pointer-events-none opacity-30',
                )}
              >
                <XIcon className="size-5" />
              </ExternalLink>
            </Tooltip>
            <Tooltip content={t('info.social.search-x')}>
              <ExternalLink
                className="text-th-fgd-2"
                href={`https://x.com/search?q=${encodeURIComponent(
                  baseToken.mint,
                )}`}
              >
                <MagnifyingGlassIcon className="size-[22px]" />
              </ExternalLink>
            </Tooltip>
            <Tooltip content={t('info.social.website')}>
              <ExternalLink
                href={websiteToShow || ''}
                className={clsx(
                  'text-th-fgd-2',
                  !websiteToShow && 'pointer-events-none opacity-30',
                )}
              >
                <GlobeAltIcon className="size-5" />
              </ExternalLink>
            </Tooltip>
            <Tooltip content={t('info.social.telegram')}>
              <ExternalLink
                href={telegramToShow || ''}
                className={clsx(
                  'text-th-fgd-2',
                  !telegramToShow && 'pointer-events-none opacity-30',
                )}
              >
                <TelegramIcon className="size-5" />
              </ExternalLink>
            </Tooltip>
          </div>
        </div>
      </div>

      {showDescModal ? (
        <DescriptionModal
          desc={descriptionToShow ?? ''}
          isOpen={showDescModal}
          onClose={() => setShowDescModal(false)}
        />
      ) : null}

      {showSelectList ? (
        <SearchModal
          isOpen={showSelectList}
          onClose={() => setShowSelectList(false)}
          onResultClick={handleSelect}
          type="quoteToken"
        />
      ) : null}
    </>
  )
}

export default TokenInfo
